import React, { useEffect, useRef, useState } from "react"
import axios, { AxiosProgressEvent } from "axios"
import { XCircleIcon } from "@heroicons/react/20/solid"

interface VideoUploaderProps {
  onUploadComplete: (urls: string[]) => void
  preExistingVideos?: string[]
  showPreviews?: boolean
  buttonLabel?: string
}

const MAX_SIZE_MB = 500
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

type VideoState = {
  file: File
  url: string
  progress: number
  isUploading: boolean
  error: string | null
}

const uploadToSignedUrl = async (
  presignedUrl: string,
  file: File,
  onProgress: (p: number) => void,
  signal?: AbortSignal,
) => {
  return axios
    .put(presignedUrl, file, {
      headers: { "Content-Type": file.type },
      onUploadProgress: (e: AxiosProgressEvent) => {
        const loaded = e?.loaded ?? 0
        const total = e?.total ?? 0
        if (total > 0) {
          onProgress(loaded / total)
        }
      },
      signal,
    })
    .then(() => undefined)
    .catch((err) => {
      if (axios.isAxiosError(err)) {
        if (err.code === "ERR_CANCELED") {
          throw new Error("Upload aborted")
        }
        if (err.response) {
          throw new Error(`Upload failed with status ${err.response.status}`)
        }
        throw new Error("Network error during upload")
      }
      throw new Error(err?.message || "Network error during upload")
    })
}

const VideoUploader: React.FC<VideoUploaderProps> = ({
  onUploadComplete,
  preExistingVideos,
  showPreviews = true,
  buttonLabel = "Video Seç",
}) => {
  const [videos, setVideos] = useState<VideoState[]>(() => {
    return (
      preExistingVideos?.map((url) => ({
        file: new File([], url.substring(url.lastIndexOf("/") + 1)),
        url: url,
        progress: 100,
        isUploading: false,
        error: null,
      })) || []
    )
  })
  const abortControllers = useRef<(AbortController | null)[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const videosRef = useRef<VideoState[]>(videos)

  useEffect(() => {
    videosRef.current = videos
  }, [videos])

  const uploadVideo = async (file: File, index: number) => {
    abortControllers.current[index] = new AbortController()
    setVideos((prev) =>
      prev.map((v, i) => (i === index ? { ...v, isUploading: true, error: null, progress: 0 } : v)),
    )
    try {
      const presignRes = await fetch(
        `${process.env.REACT_APP_AUTH_API_URL || "https://api.teamfluencer.co"}/upload/new`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ contentType: file.type }),
        },
      )
      if (!presignRes.ok) {
        throw new Error(`Presign request failed: ${await presignRes.text()}`)
      }
      const presignJson = await presignRes.json()
      const presignedUrl: string = presignJson.url
      const key: string = presignJson.key

      await uploadToSignedUrl(
        presignedUrl,
        file,
        (p) => {
          setVideos((prev) =>
            prev.map((v, i) => (i === index ? { ...v, progress: Math.round(p * 100) } : v)),
          )
        },
        abortControllers.current[index]?.signal,
      )

      const finalUrl = presignedUrl.split(key)[0] + key

      setVideos((prev) => {
        const updatedVideos = prev.map((v, i) =>
          i === index ? { ...v, url: finalUrl, isUploading: false, progress: 100 } : v,
        )
        setTimeout(() => {
          onUploadComplete(updatedVideos.filter((v) => v.url).map((v) => v.url))
        }, 1500)
        return updatedVideos
      })
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error("Video upload error:", err)
      const errorMessage =
        err instanceof Error ? err.message : "Video yükleme sırasında hata oluştu."
      setVideos((prev) =>
        prev.map((v, _i) => ({
          ...v,
          error: errorMessage,
          isUploading: false,
        })),
      )
      return
    }
  }

  const buildVideoState = (file: File): VideoState => {
    if (file.size > MAX_SIZE_BYTES) {
      return {
        file,
        url: "",
        progress: 0,
        isUploading: false,
        error: `Dosya boyutu çok büyük (${(file.size / 1024 / 1024).toFixed(1)}MB)`,
      }
    }
    return { file, url: "", progress: 0, isUploading: false, error: null }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((file) => file.type.startsWith("video/"))
    if (!files.length) {
      return
    }
    if (inputRef.current) {
      inputRef.current.value = ""
    }
    const newVideos = files.map(buildVideoState)
    const initialVideosLength = videosRef.current.length

    setVideos((prevVideos) => [...prevVideos, ...newVideos])

    newVideos.forEach((video, idx) => {
      if (!video.error) {
        uploadVideo(video.file, initialVideosLength + idx)
      }
    })
  }

  const handleDelete = (index: number) => {
    if (abortControllers.current[index]) {
      abortControllers.current[index]?.abort()
      abortControllers.current[index] = null
    }
    const updated = videos.filter((_, i) => i !== index)
    setVideos(updated)
    onUploadComplete(updated.filter((v) => v.url).map((v) => v.url))
  }

  return (
    <div className="flex flex-col items-start w-full">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="bg-[#3B82F6] text-white rounded-md h-12 min-w-[110px] px-5 font-poppins-medium hover:bg-blue-600 transition flex items-center justify-center whitespace-nowrap"
        >
          {buttonLabel}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      {showPreviews && (
        <div className="flex flex-wrap mt-4 gap-4 w-full">
          {videos.map((video, index) => (
            <div
              key={`${video.url}-${index}`}
              className="relative w-full max-w-xl aspect-video bg-black rounded-2xl shadow-lg flex items-center justify-center mb-6"
              style={{ minHeight: 220 }}
            >
              {video.error ? (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-red-500 text-sm mt-2">{video.error}</span>
                </div>
              ) : video.isUploading ? (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <div className="w-3/4 bg-gray-300 rounded h-4 mt-2">
                    <div
                      className="bg-blue-500 h-4 rounded"
                      style={{ width: `${video.progress}%`, transition: "width 0.2s" }}
                    ></div>
                  </div>
                  <span className="block text-center mt-2 text-base font-semibold text-white">
                    {video.progress}%
                  </span>
                </div>
              ) : video.url ? (
                <VideoPreview url={video.url} />
              ) : null}
              <button
                type="button"
                onClick={() => handleDelete(index)}
                className="absolute top-2 right-2 bg-black bg-opacity-60 rounded-full p-2 hover:bg-opacity-90 transition"
                style={{ zIndex: 10 }}
              >
                <XCircleIcon className="w-8 h-8 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const VideoPreview: React.FC<{ url: string }> = ({ url }) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play()
    }
  }
  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause()
    }
  }
  return (
    <video
      ref={videoRef}
      src={url}
      controls
      className="w-full h-full object-cover rounded-2xl"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ minHeight: 220, background: "#000" }}
    />
  )
}

export default VideoUploader
